import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNowStrict } from 'date-fns';
import {
  useInstructorCohorts,
  useCohortStudents,
  useCohortCourses,
  useCreateCohort,
} from '../hooks';
import { useAtRisk } from '../hooks/useAtRisk';
import { parseApiError } from '../services/apiClient';
import type { CohortInput } from '../services/cohortsApi';
import AtRiskWidget from '../components/AtRiskWidget';
import { cohortAllowsEnrollment, cohortDateRangeError } from '../lib/cohortCalendar';
import styles from './Cohorts.module.css';

type SortKey = 'name' | 'progress' | 'lastActive';
const initialForm: CohortInput = {
  name: '',
  description: '',
  courseId: '',
  capacity: 30,
  startDate: '',
  endDate: '',
};

const CreateCohort: React.FC<{
  onClose: () => void;
  onCreated: (id: string) => void;
}> = ({ onClose, onCreated }) => {
  const [form, setForm] = useState(initialForm);
  const courses = useCohortCourses();
  const create = useCreateCohort();
  const dateError = cohortDateRangeError(form.startDate, form.endDate);
  return (
    <section
      className={styles.createPanel}
      aria-labelledby="create-cohort-heading"
    >
      <h2 id="create-cohort-heading">Create a cohort</h2>
      <form
        onSubmit={async (event) => {
          event.preventDefault();
          if (dateError || create.isPending) return;
          try {
            const cohort = await create.mutateAsync(form);
            onCreated(cohort.id);
          } catch {
            /* The form remains available for correction. */
          }
        }}
      >
        <label>
          Name
          <input
            autoFocus
            required
            maxLength={200}
            value={form.name}
            onChange={(event) => setForm({ ...form, name: event.target.value })}
          />
        </label>
        <label>
          Course
          <select
            required
            value={form.courseId}
            onChange={(event) =>
              setForm({ ...form, courseId: event.target.value })
            }
          >
            <option value="">
              {courses.isLoading ? 'Loading courses…' : 'Select a course'}
            </option>
            {courses.data?.map((course) => (
              <option key={course.id} value={course.id}>
                {course.name}
              </option>
            ))}
          </select>
        </label>
        {courses.isError && (
          <p role="alert">
            Couldn&apos;t load courses.{' '}
            <button type="button" onClick={() => courses.refetch()}>
              Retry
            </button>
          </p>
        )}
        <label>
          Description
          <textarea
            rows={3}
            maxLength={4000}
            value={form.description}
            onChange={(event) =>
              setForm({ ...form, description: event.target.value })
            }
          />
        </label>
        <div className={styles.formRow}>
          <label>
            Capacity
            <input
              type="number"
              min={1}
              max={10000}
              required
              value={form.capacity}
              onChange={(event) =>
                setForm({ ...form, capacity: Number(event.target.value) })
              }
            />
          </label>
          <label>
            Start date
            <input
              type="date"
              required
              value={form.startDate}
              max={form.endDate || undefined}
              onChange={(event) =>
                setForm({ ...form, startDate: event.target.value })
              }
            />
          </label>
          <label>
            End date (optional)
            <input
              type="date"
              min={form.startDate}
              value={form.endDate}
              onChange={(event) =>
                setForm({ ...form, endDate: event.target.value })
              }
            />
          </label>
        </div>
        {dateError && <p role="alert">{dateError}</p>}
        {create.isError && (
          <p role="alert">{parseApiError(create.error).message}</p>
        )}
        <div className={styles.formRow}>
          <button
            type="submit"
            className={styles.emptyAction}
            disabled={create.isPending || !courses.data?.length || Boolean(dateError)}
          >
            {create.isPending ? 'Creating…' : 'Create cohort'}
          </button>
          <button type="button" onClick={onClose} disabled={create.isPending}>
            Cancel
          </button>
        </div>
      </form>
    </section>
  );
};

const Cohorts: React.FC = () => {
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [activeCohortId, setActiveCohortId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);
  const cohortsQuery = useInstructorCohorts({ page, limit: 50 });
  const cohorts = cohortsQuery.data?.data ?? [];
  const activeCohort =
    cohorts.find((cohort) => cohort.id === activeCohortId) ?? cohorts[0];
  const cohortId = activeCohort?.id ?? '';
  const students = useCohortStudents(cohortId);
  const risk = useAtRisk(cohortId ? { cohortId } : {});
  const roster = useMemo(() => {
    const term = search.trim().toLocaleLowerCase();
    return (students.data ?? [])
      .filter((student) =>
        `${student.name} ${student.email}`.toLocaleLowerCase().includes(term),
      )
      .sort((a, b) => {
        const direction = sortDir === 'asc' ? 1 : -1;
        if (sortKey === 'name') return direction * a.name.localeCompare(b.name);
        if (sortKey === 'progress')
          return (
            direction *
            ((a.totalLabs ? a.completedLabs / a.totalLabs : -1) -
              (b.totalLabs ? b.completedLabs / b.totalLabs : -1))
          );
        return (
          direction *
          ((new Date(a.lastActive).getTime() || 0) -
            (new Date(b.lastActive).getTime() || 0))
        );
      });
  }, [students.data, search, sortKey, sortDir]);
  const toggleSort = (key: SortKey) => {
    setSortKey(key);
    setSortDir(key === sortKey && sortDir === 'asc' ? 'desc' : 'asc');
  };
  const sortHeading = (label: string, key: SortKey) => (
    <th
      scope="col"
      aria-sort={
        sortKey === key
          ? sortDir === 'asc'
            ? 'ascending'
            : 'descending'
          : 'none'
      }
    >
      <button
        type="button"
        className={styles.sortable}
        onClick={() => toggleSort(key)}
      >
        {label}{' '}
        <span aria-hidden="true">
          {sortKey === key ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}
        </span>
      </button>
    </th>
  );
  return (
    <div className={styles.page}>
      <header className={styles.masthead}>
        <div className={styles.mastheadCopy}>
          <span className={styles.eyebrow}>Cohort dashboard</span>
          <h1 className={styles.title}>Cohorts</h1>
          {cohorts.length > 0 && (
            <select
              aria-label="Select cohort"
              className={styles.cohortSelect}
              value={cohortId}
              onChange={(event) => {
                setActiveCohortId(event.target.value);
                setSearch('');
              }}
            >
              {cohorts.map((cohort) => (
                <option key={cohort.id} value={cohort.id}>
                  {cohort.name}
                </option>
              ))}
            </select>
          )}
        </div>
        <div className={styles.headerActions}>
          {activeCohort && (
            <Link
              to={`/cohorts/${encodeURIComponent(cohortId)}`}
              className={styles.emptyAction}
            >
              Manage cohort
            </Link>
          )}
          <button
            type="button"
            className={styles.emptyAction}
            onClick={() => setShowCreate(!showCreate)}
            aria-expanded={showCreate}
          >
            Create cohort
          </button>
        </div>
      </header>
      {showCreate && (
        <CreateCohort
          onClose={() => setShowCreate(false)}
          onCreated={(id) => {
            setActiveCohortId(id);
            setPage(1);
            setShowCreate(false);
          }}
        />
      )}
      {cohortsQuery.isLoading ? (
        <p className={styles.empty} role="status">
          Loading cohorts…
        </p>
      ) : cohortsQuery.isError ? (
        <div className={styles.errorState} role="alert">
          <p>Couldn&apos;t load cohorts.</p>
          <button
            type="button"
            className={styles.retryBtn}
            onClick={() => cohortsQuery.refetch()}
          >
            Retry
          </button>
        </div>
      ) : !activeCohort ? (
        <div className={styles.empty}>
          <strong>No cohorts yet.</strong>
          <p>
            Create a cohort to organize enrollment and track course progress.
          </p>
        </div>
      ) : (
        <>
          <section
            className={styles.kpiStrip}
            aria-label="Selected cohort metrics"
          >
            <div className={styles.kpiCell}>
              <span className={styles.kpiLabel}>Enrolled</span>
              <span className={styles.kpiValue}>
                {activeCohort.studentCount}
              </span>
              <span className={styles.kpiDelta}>
                {activeCohort.capacity} {activeCohort.capacity === 1 ? 'place' : 'places'}
              </span>
            </div>
            <div className={styles.kpiCell}>
              <span className={styles.kpiLabel}>Completion</span>
              <span className={styles.kpiValue}>
                {activeCohort.completionRate}%
              </span>
              <span className={styles.kpiDelta}>
                Students completing all labs
              </span>
            </div>
            <div className={styles.kpiCell}>
              <span className={styles.kpiLabel}>Average progress</span>
              <span className={styles.kpiValue}>
                {activeCohort.avgProgress}%
              </span>
              <span className={styles.kpiDelta}>
                {activeCohort.courseName || 'Course not assigned'}
              </span>
            </div>
            <div className={styles.kpiCell}>
              <span className={styles.kpiLabel}>At risk</span>
              <span className={styles.kpiValue}>
                {risk.isLoading || risk.isError
                  ? '—'
                  : (risk.data?.length ?? 0)}
              </span>
              <span className={styles.kpiDelta}>
                {risk.isError ? 'Risk signals unavailable' : 'Selected cohort'}
              </span>
            </div>
          </section>
          <div className={styles.body}>
            <section className={styles.card} aria-label="Cohort roster">
              <header className={styles.cardHead}>
                <h2 className={styles.cardTitle}>Roster</h2>
                <span className={styles.cardMeta}>
                  {roster.length} {roster.length === 1 ? 'student' : 'students'}
                </span>
                <div className={styles.cardHeadSpacer} />
                <input
                  type="search"
                  aria-label="Search roster"
                  className={styles.search}
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search students…"
                />
              </header>
              {students.isLoading ? (
                <p className={styles.empty} role="status">
                  Loading roster…
                </p>
              ) : students.isError ? (
                <div className={styles.errorState} role="alert">
                  <p>Couldn&apos;t load this cohort&apos;s roster.</p>
                  <button
                    type="button"
                    className={styles.retryBtn}
                    onClick={() => students.refetch()}
                  >
                    Retry
                  </button>
                </div>
              ) : !roster.length ? (
                <div className={styles.empty}>
                  <strong>
                    {search ? 'No matching students.' : 'No students enrolled.'}
                  </strong>
                  <p>
                    {search
                      ? 'Try a different name or email.'
                      : activeCohort.status === 'archived'
                        ? 'This cohort is archived. New enrollment is unavailable.'
                        : 'Add students from the cohort management page.'}
                  </p>
                  {!search && cohortAllowsEnrollment(activeCohort.status) && (
                    <Link
                      to={`/cohorts/${encodeURIComponent(cohortId)}`}
                      className={styles.emptyAction}
                    >
                      Enroll students
                    </Link>
                  )}
                </div>
              ) : (
                <div className={styles.tableScroll}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        {sortHeading('Student', 'name')}
                        {sortHeading('Progress', 'progress')}
                        {sortHeading('Last active', 'lastActive')}
                        <th scope="col">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {roster.map((student) => {
                        const percentage = student.totalLabs
                          ? Math.round(
                              (student.completedLabs / student.totalLabs) * 100,
                            )
                          : null;
                        const lastActive = new Date(student.lastActive);
                        return (
                          <tr key={student.studentId}>
                            <td>
                              <div className={styles.studentCell}>
                                <span
                                  className={styles.initials}
                                  aria-hidden="true"
                                >
                                  {student.name
                                    .split(/\s+/)
                                    .map((name) => name[0])
                                    .slice(0, 2)
                                    .join('')
                                    .toUpperCase() || 'ST'}
                                </span>
                                <div className={styles.studentInfo}>
                                  <Link
                                    className={styles.studentName}
                                    to={`/students/${encodeURIComponent(student.studentId)}`}
                                  >
                                    {student.name || student.email}
                                  </Link>
                                  <span className={styles.studentEmail}>
                                    {student.email}
                                  </span>
                                </div>
                              </div>
                            </td>
                            <td>
                              <span className={styles.cellNumeric}>
                                {percentage === null ? '—' : `${percentage}%`}
                              </span>
                              <span className={styles.studentEmail}>
                                {student.completedLabs} / {student.totalLabs}{' '}
                                labs
                              </span>
                            </td>
                            <td className={styles.cellMuted}>
                              {Number.isFinite(lastActive.getTime())
                                ? formatDistanceToNowStrict(lastActive, {
                                    addSuffix: true,
                                  })
                                : '—'}
                            </td>
                            <td>
                              <span
                                className={`${styles.statusChip} ${student.status === 'at-risk' || student.status === 'behind' ? styles.statusAtRisk : styles.statusOnTrack}`}
                              >
                                {student.status.replace(/-/g, ' ')}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
            <aside>
              <AtRiskWidget compact cohortId={cohortId} />
            </aside>
          </div>
        </>
      )}
      {cohortsQuery.data && cohortsQuery.data.pagination.totalPages > 1 && (
        <nav className={styles.headerActions} aria-label="Cohort pages">
          <button
            type="button"
            className={styles.emptyAction}
            disabled={page <= 1}
            onClick={() => {
              setPage(page - 1);
              setActiveCohortId(null);
            }}
          >
            Previous
          </button>
          <span>
            Page {page} of {cohortsQuery.data.pagination.totalPages}
          </span>
          <button
            type="button"
            className={styles.emptyAction}
            disabled={page >= cohortsQuery.data.pagination.totalPages}
            onClick={() => {
              setPage(page + 1);
              setActiveCohortId(null);
            }}
          >
            Next
          </button>
        </nav>
      )}
    </div>
  );
};

export default Cohorts;
